<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAudienceStore } from '@/app/stores/audience'
import VfBiText from '@/shared/ui/VfBiText.vue'
import VfBroadcastPanel from '@/shared/ui/VfBroadcastPanel.vue'
import { localizedAudienceText } from '@/core/i18n/audienceText'
import type { BroadcastPanelStatus } from '@/shared/ui/broadcastState'
import VfGlassContainer from '@/shared/ui/VfGlassContainer.vue'
import VfActionButton from '@/shared/ui/VfActionButton.vue'
import VfIcon from '@/shared/ui/VfIcon.vue'

const route = useRoute()
const router = useRouter()
const audience = useAudienceStore()
const broadcastOpen = ref(false)
const broadcastPanel = ref<{ markTickerRead: (id: string) => void } | null>(null)
const broadcastStatus = ref<BroadcastPanelStatus>({ unreadCount: 0, ticker: null })
const tickerRoot = ref<HTMLElement | null>(null)
const tickerText = ref<HTMLElement | null>(null)
const tickerDuration = ref(10)
const t = (value: string) => localizedAudienceText(audience.config, audience.locale, value)

const isHome = computed(() => route.name === 'home')
const broadcastEnabled = computed(() => !audience.previewMode && audience.capabilities.broadcast)
watch([() => route.name, broadcastEnabled], ([name, enabled]) => {
  broadcastOpen.value = name === 'broadcasts' && enabled
  if (name === 'broadcasts' && !enabled && audience.status === 'ready') void router.replace('/mode')
}, { immediate: true })
watch(() => broadcastStatus.value.ticker?.id, async () => {
  await nextTick()
  if (!tickerRoot.value || !tickerText.value) return
  const distance = tickerRoot.value.getBoundingClientRect().width + tickerText.value.getBoundingClientRect().width
  tickerDuration.value = Math.max(0.1, distance / 55)
})
const unreadBadge = computed(() => broadcastStatus.value.unreadCount > 99 ? '99+' : String(broadcastStatus.value.unreadCount || ''))
const broadcastAriaLabel = computed(() => {
  if (audience.previewMode) return t('预览模式不连接实时公告')
  if (!broadcastStatus.value.unreadCount) return t('查看公告记录')
  const count = unreadBadge.value
  return audience.locale === 'en' ? `${t('查看公告记录')}, ${count} unread` : `${t('查看公告记录')}，${count}条未读`
})
const headerTitle = computed(() => {
  if (route.name === 'mode') return '选择场景'
  if (isHome.value) return audience.config?.projectName || '场馆数字导视助手'
  if (route.name === 'entry') return '我要入场'
  if (route.name === 'entry-notice') return audience.config?.entryNotice?.menuTitle || '入场须知'
  if (route.name === 'exit') return '我要离场'
  if (route.name === 'entry-overview') return '方位总览'
  if (route.name === 'entry-transports') return '入场指引'
  if (route.name === 'entry-transport') return audience.selectedTransport?.entry?.title || audience.selectedTransport?.name || '入场指引'
  if (route.name === 'entry-zones') return '选择看区'
  if (route.name === 'entry-zone') return `${audience.selectedZone?.name || '看区'}路线`
  if (route.name === 'exit-transport') return audience.selectedTransport?.name || '离场指引'
  if (String(route.name).startsWith('entry')) return '我要入场'
  if (String(route.name).startsWith('exit')) return '我要离场'
  if (route.name === 'services') return '其他服务'
  if (route.name === 'service-detail') {
    if (route.params.serviceId === 'order-promotion') return audience.config?.services?.orderPromotion?.title || '场馆点单服务'
    return audience.config?.services?.items?.find((item) => item.id === String(route.params.serviceId || ''))?.title || '服务详情'
  }
  if (route.name === 'lost-found') return '失物招领'
  if (route.name === 'broadcasts') return '实时公告'
  if (route.name === 'media') return '图片预览'
  if (route.name === 'error') return '页面提示'
  if (route.name === 'route-summary') return '路线详情'
  return audience.config?.projectName || '场馆数字导视助手'
})

function back() {
  const name = route.name
  const targets: Partial<Record<string, string>> = {
    mode: '/', entry: '/mode', exit: '/mode', 'entry-notice': '/entry', 'entry-overview': '/entry',
    'entry-transports': '/entry', 'entry-transport': '/entry/transports', 'entry-zones': `/entry/transports/${audience.transportId}`,
    'entry-zone': '/entry/zones', 'entry-gate': '/entry/transports', 'exit-overview': '/exit', 'exit-destinations': '/exit', 'exit-transport': '/exit', services: '/mode', 'service-detail': '/services', 'lost-found': '/mode', broadcasts: '/mode', media: '/mode', error: '/',
  }
  if (name === 'route-summary') {
    if (route.query.from === 'last-route') { router.push('/'); return }
    router.push(audience.mode === 'entry' ? `/entry/zones/${audience.zoneId}` : `/exit/transports/${audience.transportId}`)
  }
  else router.push(targets[String(name)] || '/')
}

function closeBroadcast() {
  broadcastOpen.value = false
  if (route.name === 'broadcasts') router.back()
}

function handleTickerAnimationEnd() {
  const id = broadcastStatus.value.ticker?.id
  if (id) broadcastPanel.value?.markTickerRead(id)
}
</script>

<template>
  <main class="vf-phone-shell" :class="{ 'is-home': isHome }" :aria-label="t('场馆数字导视助手')">
    <VfGlassContainer as="header" class="vf-mini-bar" variant="strong">
      <VfActionButton class="vf-bar-icon" variant="icon" :aria-label="t(isHome ? '返回首页' : '返回上一页')" @click="isHome ? router.push('/') : back()">
        <VfIcon :name="isHome ? 'home' : 'back'" />
      </VfActionButton>
      <div class="vf-mini-title vf-broadcast-title-target" :class="{ 'vf-broadcast-playing': broadcastEnabled && broadcastStatus.ticker }"><strong><VfBiText :text="headerTitle" variant="topbar" /></strong><span v-if="broadcastEnabled && broadcastStatus.ticker" :key="broadcastStatus.ticker.id" ref="tickerRoot" class="vf-broadcast-title-ticker" :class="broadcastStatus.ticker.level" aria-live="assertive"><span class="vf-broadcast-marquee"><span ref="tickerText" :style="{ '--vf-broadcast-duration': `${tickerDuration}s` }" @animationend="handleTickerAnimationEnd">{{ broadcastStatus.ticker.text }}</span></span></span></div>
      <div class="vf-topbar-actions">
        <VfActionButton v-if="broadcastEnabled" class="vf-mini-menu" variant="icon" :data-broadcast-unread="unreadBadge || undefined" :aria-label="broadcastAriaLabel" @click="router.push('/broadcasts')"><VfIcon name="bell" /><span v-if="unreadBadge" class="vf-broadcast-unread" aria-hidden="true">{{ unreadBadge }}</span></VfActionButton>
      </div>
    </VfGlassContainer>
    <section class="vf-app-view"><slot /></section>
    <VfBroadcastPanel v-if="broadcastEnabled" ref="broadcastPanel" :open="broadcastOpen" @close="closeBroadcast" @status="broadcastStatus = $event" />
  </main>
</template>
